import { create } from 'zustand';
import { MODES, type Asset } from '@scio3/shared-types';

/** Subset of MODES used in asset list: default browsing, shared folders, favorites, or cloud drive view */
type AssetListMode =
  | typeof MODES.DEFAULT
  | typeof MODES.SHARE_WITH_ME
  | typeof MODES.FAVORITES
  | typeof MODES.DRIVE;

interface AssetFilterState {
  fileType: string[];
  tags: string[];
  searchText: string;
  /**
   * Where a search looks. `folder` (the default) confines it to the location you are standing in;
   * `all` widens it to the whole account. Only meaningful inside a folder or a virtual location —
   * at the root the two are the same thing.
   *
   * Folder-first because navigating into a folder IS an expression of where you want to look.
   * Widening is one click on the scope chip in the search field, and it navigates out rather than
   * searching wide in place, so the location never contradicts the results. Mirrors Screens.
   */
  searchScope: 'all' | 'folder';
  /** Navigation mode: SHARE_WITH_ME shows shared folders, DRIVE browses cloud storage asset children */
  mode: AssetListMode;
  /** Root cloud storage asset when in DRIVE mode (Google Drive, OneDrive, Dropbox) */
  driveAsset: Asset | null;
  /** Path before entering DRIVE mode — restored on Close */
  preDrivePath: string | null;

  setFileType: (fileType: string[]) => void;
  setTags: (tags: string[]) => void;
  setSearchText: (text: string) => void;
  setSearchScope: (scope: 'all' | 'folder') => void;
  setMode: (mode: AssetListMode) => void;
  clearFilters: () => void;
  hasActiveFilters: () => boolean;
  /** Enter DRIVE mode: browse child assets of a cloud storage asset */
  enterDriveMode: (asset: Asset, currentPath: string) => void;
  /** Exit DRIVE mode: returns previous path for navigation */
  exitDriveMode: () => { previousPath: string };
}

export const useAssetFilterStore = create<AssetFilterState>((set, get) => ({
  fileType: [],
  tags: [],
  searchText: '',
  searchScope: 'folder',
  mode: MODES.DEFAULT,
  driveAsset: null,
  preDrivePath: null,

  setFileType: (fileType) =>
    set((state) => ({
      fileType,
      // Type filter applies WITHIN the current location for DRIVE and FAVORITES, so preserve
      // those modes — the query composes fileType with drive children / favoritesOnly.
      // SHARE_WITH_ME is folders-only (no assets to type-filter), so it still resets to DEFAULT.
      mode:
        state.mode === MODES.DRIVE || state.mode === MODES.FAVORITES ? state.mode : MODES.DEFAULT,
    })),
  setTags: (tags) =>
    set((state) => ({
      tags,
      // Mirror setFileType: tags apply WITHIN DRIVE and FAVORITES (query composes tags with those),
      // but SHARE_WITH_ME is folders-only (no assets to tag-filter), so a tag toggle there exits to
      // Home with the tag applied — otherwise the click would be a dead no-op (assets query skipped).
      mode:
        state.mode === MODES.DRIVE || state.mode === MODES.FAVORITES ? state.mode : MODES.DEFAULT,
    })),
  setSearchText: (text) => {
    if (get().searchText !== text) {
      // Preserve the current location while searching (SHARE_WITH_ME / FAVORITES / DRIVE) so the
      // search-scope toggle ("Search in: All / <location>") is meaningful — searching within
      // Shared with me / Favorites must NOT kick the user back to Home. Mirrors Playlists/Schedules,
      // which keep their mode on search. The data layer (useAssets) decides scope via searchScope.
      set({ searchText: text });
    }
  },
  setSearchScope: (scope) => {
    if (get().searchScope !== scope) set({ searchScope: scope });
  },
  setMode: (mode) =>
    set({
      mode,
      fileType: [],
      tags: [],
      searchText: '',
      searchScope: 'folder',
      driveAsset: null,
      preDrivePath: null,
    }),
  // Scope resets with everything else. Letting it survive made "search all" a sticky account-wide
  // preference: widen once in one folder and every later folder silently searched the whole
  // account, with nothing on screen saying why.
  clearFilters: () =>
    set({
      fileType: [],
      tags: [],
      searchText: '',
      searchScope: 'folder',
      mode: MODES.DEFAULT,
      driveAsset: null,
      preDrivePath: null,
    }),
  hasActiveFilters: () => {
    const s = get();
    return (
      s.fileType.length > 0 ||
      s.tags.length > 0 ||
      s.searchText.length > 0 ||
      s.mode === MODES.SHARE_WITH_ME ||
      s.mode === MODES.FAVORITES
    );
  },
  enterDriveMode: (asset, currentPath) =>
    set({
      mode: MODES.DRIVE,
      driveAsset: asset,
      preDrivePath: currentPath,
      searchText: '',
      fileType: [],
      tags: [],
    }),
  exitDriveMode: () => {
    const { preDrivePath } = get();
    set({
      mode: MODES.DEFAULT,
      driveAsset: null,
      preDrivePath: null,
    });
    return { previousPath: preDrivePath ?? '/' };
  },
}));
